import React, { useState, useCallback } from 'react'

import MultiSelect from '@khanacademy/react-multi-select'

import { array, object, number, string, shape, func, bool } from 'prop-types'

import Icon from 'Pods/Shared/Icon/Icon'

import { cgtStandardOptions } from 'Utils/cgtStandardOptions'

import BulkActions from './BulkActions'
import CGTStandardFilter from './CGTStandardFilter'
import { productTypeLabel, productTypeOptions } from './productTypeOptions'

const statusOptions = [
  { value: 'published', label: 'Published' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending' },
]

const DEFAULT_FILTERS = {
  fileType: [],
  page: 1,
  perPage: 20,
  query: '',
  status: [],
  productType: [],
  cgtStandard: {},
}

function FiltersAndBulkActions({
  onFiltersChange,
  filters = DEFAULT_FILTERS,
  meta = {
    availableFileTypes: [],
    fileTypeFilterEnabled: false,
    currentPage: 1,
    perPage: 20,
    sortBy: [],
    totalCount: 0,
    totalPages: 0,
  },
  disableFilters,
  selectedItems,
  editableCollectionId,
  editCollection,
  isAddingToCollection,
  triggerAddToCollection,
  onShowSelectedTrigger,
  showOnlySelected,
  clearSelection,
  cgtStandardFiltersEnabled = false,
  cadProductTypeEnabled = false,
}) {
  const [query, setQuery] = useState(filters.query)
  const fileTypeFilterEnabled = meta?.fileTypeFilterEnabled
  const availableFileTypes = fileTypeFilterEnabled ? meta.availableFileTypes || [] : []

  const handleQueryChange = useCallback((event) => {
    setQuery(event.target.value)
  }, [])

  const handleSubmit = useCallback((event) => {
    event.preventDefault()
    onFiltersChange({
      ...filters,
      query,
      page: 1,
    })
  }, [onFiltersChange, filters, query])

  const handleFilterChange = useCallback((filterKey, value) => {
    onFiltersChange({
      ...filters,
      page: 1,
      [filterKey]: value,
    })
  }, [onFiltersChange, filters])

  return (
    <div className="row products-filtering__filters">
      <div className="col-12 col-md-6 col-lg-5 col-xl-6 search-container">
        <form onSubmit={handleSubmit} className="form-inline products-filtering__filters-form">
          <div className="search-form">
            <input
              type="text"
              placeholder="Search by Keywords"
              value={query}
              onChange={handleQueryChange}
              className="form-control"
              disabled={disableFilters}
            />
            <button
              type="submit"
              className="btn btn-transparent"
              disabled={disableFilters}
            >
              <Icon type="search" color="green-dark" className="info-icon" notSpaced />
            </button>
          </div>

          {fileTypeFilterEnabled && (
            <MultiSelect
              options={availableFileTypes}
              onSelectedChanged={(value) => handleFilterChange('fileType', value)}
              selected={filters.fileType}
              placeholder="Formats"
              disabled={disableFilters}
              hasSelectAll={false}
            />
          )}

          <MultiSelect
            options={statusOptions}
            onSelectedChanged={(value) => handleFilterChange('status', value)}
            selected={filters.status}
            placeholder="Status"
            disableSearch
            disabled={disableFilters}
            hasSelectAll={false}
          />

          <MultiSelect
            options={productTypeOptions(cadProductTypeEnabled)}
            onSelectedChanged={(value) => handleFilterChange('productType', value)}
            selected={filters.productType}
            placeholder={productTypeLabel('placeholder')}
            disableSearch
            disabled={disableFilters}
            hasSelectAll={false}
          />

          {cgtStandardFiltersEnabled && (
            <CGTStandardFilter
              options={cgtStandardOptions}
              onSelectedChanged={(value) => handleFilterChange('cgtStandard', value)}
              selected={filters.cgtStandard || {}}
              placeholder="CGT Standard"
              disabled={disableFilters}
            />
          )}
        </form>
      </div>

      <div className="col-12 col-md-6 col-lg-7 col-xl-6 u-text-right">
        <BulkActions
          selectedItems={selectedItems}
          onFiltersChange={onFiltersChange}
          editableCollectionId={editableCollectionId}
          editCollection={editCollection}
          isAddingToCollection={isAddingToCollection}
          triggerAddToCollection={triggerAddToCollection}
          onShowSelectedTrigger={onShowSelectedTrigger}
          showOnlySelected={showOnlySelected}
          clearSelection={clearSelection}
          cadProductTypeEnabled={cadProductTypeEnabled}
        />
      </div>
    </div>
  )
}

FiltersAndBulkActions.propTypes = {
  onFiltersChange:func.isRequired,
  filters: shape({
    fileType: array,
    page: number,
    perPage: number,
    query: string,
    status: array,
    productType: array,
    cgtStandard: object,
  }),
  meta: shape({
    availableFileTypes: array,
    fileTypeFilterEnabled: bool,
    currentPage: number,
    perPage: number,
    sortBy: array,
    totalCount: number,
    totalPages: number,
  }),
  disableFilters: bool.isRequired,
  selectedItems: array.isRequired,
  editableCollectionId: string.isRequired,
  editCollection: func.isRequired,
  isAddingToCollection: bool.isRequired,
  triggerAddToCollection: func.isRequired,
  onShowSelectedTrigger: func.isRequired,
  showOnlySelected: bool.isRequired,
  clearSelection: func.isRequired,
  cgtStandardFiltersEnabled: bool,
  cadProductTypeEnabled: bool,
}

export default FiltersAndBulkActions
